// Exporting a MizuIQ answer - its text and the data it came with - as an
// Excel workbook, CSV files or a PDF (each answer's Export menu,
// ChatbotWidget). Built in the browser from what the answer holds, never
// from the screen: its Markdown (chatMarkdownBlocks.js), its figures, charts
// and tables (visuals - the API's own values, every row the answer has) and
// its Super Mode query. The spreadsheet and PDF libraries load with the
// first export.
//   Excel  an "Answer" sheet - the question, when, district, source, the
//          text, the query, the data sheets - then a sheet a dataset, its
//          values typed (numbers, money, percentages, days)
//   CSV    a dataset a file, its values as they are; for an answer with
//          none, the question and answer as one row
//   PDF    the answer as a document, on A4: its text, figures, charts
//          (drawn), tables (all their rows) and query
import { STRENGTHS, parseBlocks } from "./chatMarkdownBlocks";
import { answerSource, splitFollowUps } from "./chatAnswer";
import { axisValue, dayOf, formatCell, formatValue, formatX, isNumber } from "./chatValues";

// ── The answer, as exported ─────────────────────────────────────────────────

// Inline Markdown as plain text: [label](url) as "label (url)", the
// emphasis and code marks let go.
function plain(text) {
  return String(text ?? "")
    .replace(/\[([^\]\n]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g, (_, label, href) => (label === href ? href : `${label} (${href})`))
    .replace(/(`+)([\s\S]*?[^`])\1(?!`)/g, "$2")
    .replace(/(?<!\\)\*\*(?=\S)([\s\S]*?\S)\*\*/g, "$1")
    .replace(/(?<![\\\w])__(?=\S)([\s\S]*?\S)__(?!\w)/g, "$1")
    .replace(/(?<!\\)~~(?=\S)([\s\S]*?\S)~~/g, "$1")
    .replace(/(?<![\\*])\*(?=[^\s*])([^*]*?[^\s*])\*(?!\*)/g, "$1")
    .replace(/(?<![\\\w])_(?=[^\s_])([^_]*?[^\s_])_(?!\w)/g, "$1")
    .replace(/\\([\\`*_{}[\]()#+\-.!|~>])/g, "$1");
}

const dateOf = (at) => {
  const date = at ? new Date(at) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};
const stamp = (date) =>
  date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

/**
 * An answer (a chat window message) as exported, with the question it
 * answers: { question, heading, kind, answeredAt, district, source, items,
 * sql } - `items` its content in the order the window shows it: Markdown
 * blocks ({ block }) and, after the opening paragraph, its figures, charts
 * and tables ({ visual }).
 */
export function answerDocument(message, question) {
  const { body } = splitFollowUps(message?.content);
  const visuals = (Array.isArray(message?.visuals) ? message.visuals : []).filter((visual) => visual && typeof visual === "object");
  const parsed = parseBlocks(body.replace(/\r\n?/g, "\n").replace(/\t/g, "    ").split("\n"));
  // The window leaves out the answer's own tiles when the server's are shown
  // (ChatMarkdown hideStats), and puts the visuals where ChatMarkdown does.
  const blocks = visuals.some((visual) => visual.kind === "kpis") ? parsed.filter((block) => block.type !== "stats") : parsed;
  const at = blocks[0]?.type === "paragraph" ? (blocks[1]?.type === "stats" ? 2 : 1) : 0;
  const investigation = message?.investigation && typeof message.investigation === "object" ? message.investigation : null;
  const sql = message?.sql && typeof message.sql === "object" && typeof message.sql.sql === "string" ? message.sql : null;
  return {
    question: String(question || "").trim(),
    heading: investigation?.title || sql?.title || null,
    kind: investigation ? "Root cause analysis" : sql?.kind === "result" ? "Super Mode query result" : sql ? "Super Mode query" : null,
    answeredAt: dateOf(message?.at),
    district: typeof message?.district === "string" && message.district ? message.district : null,
    source: answerSource(message?.tools, investigation),
    items: [
      ...blocks.slice(0, at).map((block) => ({ block })),
      ...visuals.map((visual) => ({ visual })),
      ...blocks.slice(at).map((block) => ({ block })),
    ],
    sql,
  };
}

// ── Its data ────────────────────────────────────────────────────────────────

const X_LABELS = { date: "Date", week: "Week of", month: "Month", hour: "Hour", category: "Category" };
const UNIT_FORMATS = { "%": "percent", $: "money", USD: "money" };

function chartDataset(visual) {
  const series = (Array.isArray(visual.series) ? visual.series : []).filter((s) => s && s.key);
  const data = (Array.isArray(visual.data) ? visual.data : []).filter((point) => point && typeof point === "object");
  if (!series.length || !data.length) return null;
  const format = UNIT_FORMATS[visual.unit] || (visual.unit === "gal" ? "gallons" : "decimal");
  const shown = (label) => (visual.unit && !UNIT_FORMATS[visual.unit] ? `${label} (${visual.unit})` : label);
  return {
    name: visual.title || "Chart",
    columns: [
      { key: "x", label: X_LABELS[visual.x_kind] || "Label", format: ["date", "week", "month"].includes(visual.x_kind) ? "date" : "text" },
      ...series.map((s) => ({ key: s.key, label: shown(s.label || s.key), format })),
    ],
    rows: data,
  };
}

function figuresDataset(name, items) {
  const rows = items.filter((item) => item && (item.value || item.label)).map((item) => ({ label: plain(item.label), value: plain(item.value), note: plain(item.note || "") }));
  if (!rows.length) return null;
  const columns = [{ key: "label", label: "Figure", format: "text" }, { key: "value", label: "Value", format: "auto" }];
  if (rows.some((row) => row.note)) columns.push({ key: "note", label: "Change", format: "text" });
  return { name, columns, rows };
}

function tableDataset(visual) {
  const columns = (Array.isArray(visual.columns) ? visual.columns : []).filter((column) => column && column.key);
  const rows = (Array.isArray(visual.rows) ? visual.rows : []).filter((row) => row && typeof row === "object");
  if (!columns.length || !rows.length) return null;
  const total = Number(visual.total) || rows.length;
  const note = visual.more ? `${rows.length.toLocaleString()} rows - the query had more` : total > rows.length ? `the first ${rows.length.toLocaleString()} of ${total.toLocaleString()} rows` : null;
  return { name: visual.title || "Table", columns: columns.map((c) => ({ key: c.key, label: c.label || c.key, format: c.format || "text" })), rows, note };
}

function markdownTable(block) {
  if (!block.header?.length || !block.rows?.length) return null;
  const columns = block.header.map((label, n) => ({ key: `c${n}`, label: plain(label) || `Column ${n + 1}`, format: "auto" }));
  return { name: "Table", columns, rows: block.rows.map((row) => Object.fromEntries(columns.map((c, n) => [c.key, plain(row[n] ?? "")]))) };
}

function causesDataset(block) {
  return {
    name: "Likely causes",
    columns: [{ key: "strength", label: "Strength", format: "text" }, { key: "cause", label: "Cause", format: "text" }, { key: "evidence", label: "Evidence", format: "text" }],
    rows: block.items.map((item) => ({ strength: STRENGTHS.get(item.strength)?.label || item.strength, cause: plain(item.cause), evidence: plain(item.evidence) })),
  };
}

function datasetOf(item) {
  const { visual, block } = item;
  if (visual?.kind === "kpis") return figuresDataset(visual.title || "Figures", Array.isArray(visual.items) ? visual.items : []);
  if (visual?.kind === "chart") return chartDataset(visual);
  if (visual?.kind === "table") return tableDataset(visual);
  if (block?.type === "table") return markdownTable(block);
  if (block?.type === "stats") return figuresDataset("Figures", block.items);
  if (block?.type === "causes") return causesDataset(block);
  if (block?.type === "chart") return block.visuals?.[0] ? chartDataset(block.visuals[0]) : null;
  return null;
}

/** The answer's data, each { name (unique), columns: [{ key, label, format }],
 * rows, note? } - its figures, charts' points, tables (a Super Mode query's
 * rows among them) and the tables, figures and causes its text lists. */
export function answerDatasets(doc) {
  const seen = new Map();
  return doc.items
    .map(datasetOf)
    .filter(Boolean)
    .map((dataset) => {
      const base = String(dataset.name).trim() || "Data";
      const n = (seen.get(base.toLowerCase()) || 0) + 1;
      seen.set(base.toLowerCase(), n);
      return { ...dataset, name: n > 1 ? `${base} (${n})` : base };
    });
}

// ── Its text ────────────────────────────────────────────────────────────────

// The text a line each, for a sheet and a CSV cell: lists marked, tables'
// rows with " | " between cells.
function textLines(blocks, indent = "") {
  const lines = [];
  for (const block of blocks) {
    if (block.type === "heading" || block.type === "paragraph") {
      (block.type === "heading" ? [block.text] : block.lines).forEach((line) => lines.push(indent + plain(line)));
    } else if (block.type === "list") {
      block.items.forEach((item, n) => {
        const marker = block.ordered ? `${block.start + n}. ` : "• ";
        const [first, ...rest] = textLines(item, "");
        lines.push(`${indent}${marker}${first ?? ""}`);
        rest.forEach((line) => lines.push(`${indent}${" ".repeat(marker.length)}${line}`));
      });
    } else if (block.type === "quote") {
      textLines(block.children, "").forEach((line) => lines.push(`${indent}> ${line}`));
    } else if (block.type === "code") {
      block.text.split("\n").forEach((line) => lines.push(indent + line));
    } else if (block.type === "table") {
      [block.header, ...block.rows].forEach((row) => lines.push(indent + row.map(plain).join(" | ")));
    } else if (block.type === "stats") {
      block.items.forEach((item) => lines.push(`${indent}${plain(item.value)} - ${plain(item.label)}`));
    } else if (block.type === "causes") {
      block.items.forEach((item) => lines.push(`${indent}${STRENGTHS.get(item.strength)?.label || item.strength}: ${plain(item.cause)}${item.evidence ? ` - ${plain(item.evidence)}` : ""}`));
    } else if (block.type === "chart") {
      lines.push(`${indent}[Chart: ${block.visuals?.[0]?.title || "chart"}]`);
    } else if (block.type === "rule") {
      lines.push(`${indent}---`);
    }
  }
  return lines;
}

const answerText = (doc) => textLines(doc.items.filter((item) => item.block).map((item) => item.block));

// ── Files ───────────────────────────────────────────────────────────────────

/** The answer's file name, without its extension: "mizuiq-2026-11-04-which-meters-leaked". */
export function exportName(doc) {
  const date = doc.answeredAt || new Date();
  const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const slug = String(doc.heading || doc.question || "answer")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 48)
    .replace(/^-+|-+$/g, "");
  return `mizuiq-${day}-${slug || "answer"}`;
}

/** A file handed to the browser to save. */
export function saveFile(blob, name) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

/** Rows as CSV - their values as they are, not as shown. A text that a
 * spreadsheet would run as a formula (=, +, -, @ first) is kept text. */
export function csvText(columns, rows) {
  const cell = (value) => {
    let text = value === null || value === undefined ? "" : String(value);
    if (/^[=+\-@\t\r]/.test(text) && !isNumber(text)) text = `'${text}`;
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = [columns.map((column) => cell(column.label)).join(",")];
  rows.forEach((row) => lines.push(columns.map((column) => cell(row?.[column.key])).join(",")));
  // A byte-order mark first, so spreadsheet programs read it as UTF-8.
  return String.fromCharCode(0xfeff) + lines.join("\r\n");
}

const csvBlob = (columns, rows) => new Blob([csvText(columns, rows)], { type: "text/csv;charset=utf-8" });

const fileSlug = (text) => String(text).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "data";

function exportCsv(doc, dataset) {
  if (dataset) {
    saveFile(csvBlob(dataset.columns, dataset.rows), `${exportName(doc)}-${fileSlug(dataset.name)}.csv`);
    return;
  }
  const columns = ["Question", "Answer", "Answered", "District", "Source"].map((label) => ({ key: label, label }));
  const row = { Question: doc.question, Answer: answerText(doc).join("\n"), Answered: doc.answeredAt?.toISOString() || "", District: doc.district || "", Source: doc.source || "" };
  saveFile(csvBlob(columns, [row]), `${exportName(doc)}.csv`);
}

// ── Excel ───────────────────────────────────────────────────────────────────

const EXCEL_FORMATS = { money: '"$"#,##0.00', percent: '0.0"%"', gallons: "#,##0", number: "#,##0.0", rate: "#,##0.0", decimal: "#,##0.####" };
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);

// A value as a typed cell: numbers and days as numbers with their format, a
// written figure ("$1,234", "92%", "1,234") read as one when it is all number.
function excelCell(value, format) {
  if (value === null || value === undefined || value === "") return null;
  if (format === "date") {
    const day = dayOf(value);
    if (day && /^\d{4}-\d{2}-\d{2}$/.test(String(value).slice(0, 10)) && String(value).length <= 10) {
      return { t: "n", v: (day.getTime() - EXCEL_EPOCH) / 86400000, z: "mmm d, yyyy" };
    }
    return { t: "s", v: String(value) };
  }
  if (format === "auto" && typeof value === "string") {
    const match = /^\s*(\$)?\s*(-?[\d,]*\.?\d+)\s*(%)?\s*$/.exec(value);
    if (match && /^-?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?$/.test(match[2])) {
      const n = Number(match[2].replace(/,/g, ""));
      return { t: "n", v: n, z: match[1] ? EXCEL_FORMATS.money : match[3] ? EXCEL_FORMATS.percent : n % 1 ? "#,##0.##" : "#,##0" };
    }
    return { t: "s", v: value };
  }
  if (EXCEL_FORMATS[format] && isNumber(value)) return { t: "n", v: Number(value), z: EXCEL_FORMATS[format] };
  if (typeof value === "number") return { t: "n", v: value };
  if (typeof value === "boolean") return { t: "b", v: value };
  return { t: "s", v: typeof value === "object" ? JSON.stringify(value) : String(value) };
}

// A sheet from rows of cells (null: empty), its columns as wide as their
// text (to 60), filterable from its header when `filter`.
function sheetOf(XLSX, grid, filter = false) {
  const sheet = {};
  let width = 1;
  const widths = [];
  grid.forEach((row, r) =>
    row.forEach((cell, c) => {
      if (!cell) return;
      sheet[XLSX.utils.encode_cell({ r, c })] = cell;
      width = Math.max(width, c + 1);
      const text = cell.z && cell.t === "n" ? String(Math.round(cell.v)).length + 4 : String(cell.v).split("\n")[0].length;
      widths[c] = Math.min(60, Math.max(widths[c] || 8, text + 2));
    }),
  );
  const ref = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(0, grid.length - 1), c: width - 1 } });
  sheet["!ref"] = ref;
  sheet["!cols"] = Array.from({ length: width }, (_, c) => ({ wch: widths[c] || 8 }));
  if (filter && grid.length > 1) sheet["!autofilter"] = { ref };
  return sheet;
}

const text = (value) => (value === null || value === undefined || value === "" ? null : { t: "s", v: String(value) });

// A sheet's name: at most 31 characters, none of []:*?/\, each once.
function sheetNames(names) {
  const used = new Set(["answer"]);
  return names.map((name) => {
    const base = String(name).replace(/[[\]:*?/\\]/g, " ").replace(/\s+/g, " ").trim().slice(0, 28) || "Data";
    let candidate = base;
    for (let n = 2; used.has(candidate.toLowerCase()); n += 1) candidate = `${base.slice(0, 26)} ${n}`;
    used.add(candidate.toLowerCase());
    return candidate;
  });
}

async function exportExcel(doc, datasets) {
  const XLSX = await import("xlsx");
  const book = XLSX.utils.book_new();
  const names = sheetNames(datasets.map((dataset) => dataset.name));
  const grid = [[text("MizuIQ answer")], []];
  const field = (label, value) => {
    if (value) grid.push([text(label), typeof value === "object" ? value : text(value)]);
  };
  field("Question", doc.question);
  if (doc.heading && doc.heading !== doc.question) field("Title", doc.heading);
  field("Kind", doc.kind);
  field("Answered", doc.answeredAt ? text(stamp(doc.answeredAt)) : null);
  field("District", doc.district);
  field("Source", doc.source);
  grid.push([], [text("Answer")]);
  answerText(doc).forEach((line) => grid.push([null, text(line)]));
  if (doc.sql) {
    grid.push([], [text("Query"), text(doc.sql.sql)]);
    field("Query status", sqlStatus(doc.sql));
  }
  if (datasets.length) {
    grid.push([], [text("Data"), text("Sheet"), text("Rows")]);
    datasets.forEach((dataset, n) =>
      grid.push([text(dataset.name), text(names[n]), { t: "n", v: dataset.rows.length }, text(dataset.note ? `(${dataset.note})` : null)]),
    );
  }
  XLSX.utils.book_append_sheet(book, sheetOf(XLSX, grid), "Answer");
  datasets.forEach((dataset, n) => {
    const rows = [dataset.columns.map((column) => text(column.label)), ...dataset.rows.map((row) => dataset.columns.map((column) => excelCell(row?.[column.key], column.format)))];
    XLSX.utils.book_append_sheet(book, sheetOf(XLSX, rows, true), names[n]);
  });
  const bytes = XLSX.write(book, { bookType: "xlsx", type: "array", compression: true });
  saveFile(new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${exportName(doc)}.xlsx`);
}

function sqlStatus(sql) {
  if (sql.kind === "result" && sql.status === "ran") {
    const rows = Number(sql.row_count) || 0;
    return `Ran${dateOf(sql.ran_at) ? ` ${stamp(dateOf(sql.ran_at))}` : ""}, read-only - ${rows.toLocaleString()}${sql.truncated ? "+" : ""} ${rows === 1 ? "row" : "rows"}`;
  }
  if (sql.status === "failed") return `Failed${sql.error ? `: ${sql.error}` : ""}`;
  if (sql.status === "rejected") return "Not run - it did not pass Super Mode's checks";
  if (sql.status === "ran") return "Run";
  return "Proposed - not run";
}

// ── PDF ─────────────────────────────────────────────────────────────────────

// The PDF's fonts (Helvetica, Courier) have the Windows-1252 characters:
// others as ASCII where there is a fair one, else left out.
const CP1252 = "€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ";
const SWAPS = { "≥": ">=", "≤": "<=", "≠": "!=", "≈": "~", "→": "->", "←": "<-", "↑": "^", "↓": "v", "−": "-", "‑": "-", "✓": "v", "✔": "v", "✗": "x", "✕": "x", " ": " ", " ": " ", " ": " ", "​": "" };
function safe(value) {
  return Array.from(String(value ?? ""))
    .map((ch) => {
      if (ch in SWAPS) return SWAPS[ch];
      const code = ch.codePointAt(0);
      if (ch === "\n" || (code >= 0x20 && code < 0x7f) || (code >= 0xa0 && code <= 0xff) || CP1252.includes(ch)) return ch;
      return "";
    })
    .join("");
}

// Print colours for the chat's (ChatVisuals: theirs are for its dark ground).
const PRINT = { accent: [24, 145, 166], amber: [205, 132, 22], green: [52, 145, 88], violet: [118, 86, 210], red: [205, 76, 68], blue: [52, 104, 210], slate: [100, 116, 139] };
const SERIES_ORDER = ["accent", "amber", "green", "violet", "red", "blue"];
const SLICE_COLORS = [PRINT.accent, PRINT.amber, PRINT.green, PRINT.violet, PRINT.red, PRINT.blue, [214, 86, 148], PRINT.slate];
const INK = { text: [30, 41, 59], muted: [100, 116, 139], faint: [148, 163, 184], line: [226, 232, 240], fill: [241, 245, 249], brand: [24, 145, 166] };
const TONES = { good: PRINT.green, warn: PRINT.amber, alert: PRINT.red };
const NUMERIC = new Set(["number", "decimal", "gallons", "money", "percent", "rate"]);

// 1, 2, 2.5 or 5 times a power of ten: a scale's step.
function niceStep(span) {
  const raw = span / 4 || 1;
  const power = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / power;
  return (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 2.5 ? 2.5 : unit <= 5 ? 5 : 10) * power;
}

async function exportPdf(doc) {
  const [{ jsPDF }, tables] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  const autoTable = tables.autoTable || tables.default;
  const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 42;
  const FOOT = 30;
  const width = W - 2 * M;
  let y = M;

  const ensure = (height) => {
    if (y + height > H - M - FOOT / 2) {
      pdf.addPage();
      y = M;
    }
  };
  const font = (size, style = "normal", color = INK.text, face = "helvetica") => {
    pdf.setFont(face, style);
    pdf.setFontSize(size);
    pdf.setTextColor(...color);
  };
  // Text wrapped to the width, a page broken where it must be; `hang`: the
  // marker of a list item, at the indent, the text after it.
  const write = (value, { size = 10, style = "normal", color = INK.text, indent = 0, gap = 5, face = "helvetica", hang = null } = {}) => {
    font(size, style, color, face);
    const at = indent + (hang ? 14 : 0);
    const lines = pdf.splitTextToSize(safe(value), width - at);
    const height = size * 1.38;
    lines.forEach((line, n) => {
      ensure(height);
      if (hang && n === 0) pdf.text(safe(hang), M + indent, y, { baseline: "top" });
      pdf.text(line, M + at, y, { baseline: "top" });
      y += height;
    });
    y += gap;
  };
  const rule = (color = INK.line) => {
    ensure(8);
    pdf.setDrawColor(...color);
    pdf.setLineWidth(0.6);
    pdf.line(M, y, M + width, y);
    y += 10;
  };
  const table = (head, body, { align = [], note = null } = {}) => {
    autoTable(pdf, {
      startY: y,
      head: [head.map(safe)],
      body: body.map((row) => row.map(safe)),
      margin: { left: M, right: M, top: M, bottom: M + FOOT / 2 },
      theme: "grid",
      styles: { font: "helvetica", fontSize: 8, cellPadding: 3.5, textColor: INK.text, lineColor: INK.line, lineWidth: 0.5, overflow: "linebreak" },
      headStyles: { fillColor: INK.fill, textColor: INK.text, fontStyle: "bold" },
      columnStyles: Object.fromEntries(align.map((value, n) => [n, value ? { halign: value } : {}])),
    });
    y = (pdf.lastAutoTable?.finalY ?? y) + 6;
    if (note) write(note, { size: 7.5, color: INK.muted, gap: 4 });
    y += 6;
  };
  const caption = (title, subtitle) => {
    ensure(40);
    if (title) write(title, { size: 10.5, style: "bold", gap: subtitle ? 1 : 5 });
    if (subtitle) write(subtitle, { size: 8, color: INK.muted, gap: 5 });
  };

  // Figures as tiles, three a row.
  const tiles = (items) => {
    const list = items.filter((item) => item && (item.value || item.label));
    const across = Math.min(3, list.length) || 1;
    const gap = 8;
    const tileW = (width - gap * (across - 1)) / across;
    const tileH = list.some((item) => item.note) ? 54 : 44;
    for (let n = 0; n < list.length; n += across) {
      ensure(tileH + gap);
      list.slice(n, n + across).forEach((item, k) => {
        const x = M + k * (tileW + gap);
        pdf.setDrawColor(...INK.line);
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(x, y, tileW, tileH, 4, 4, "FD");
        font(13, "bold", TONES[item.tone] || INK.text);
        pdf.text(pdf.splitTextToSize(safe(plain(item.value)), tileW - 16)[0] || "", x + 8, y + 7, { baseline: "top" });
        font(7.5, "normal", INK.muted);
        pdf.text(pdf.splitTextToSize(safe(plain(item.label)), tileW - 16)[0] || "", x + 8, y + 26, { baseline: "top" });
        if (item.note) {
          font(7, "normal", TONES[item.note_tone] || INK.faint);
          pdf.text(pdf.splitTextToSize(safe(plain(item.note)), tileW - 16)[0] || "", x + 8, y + 38, { baseline: "top" });
        }
      });
      y += tileH + gap;
    }
    y += 4;
  };

  const seriesColor = (s, n) => PRINT[s.color] || PRINT[SERIES_ORDER[n % SERIES_ORDER.length]];

  // A filled polygon, from its points.
  const polygon = (points) => {
    if (points.length < 3) return;
    const deltas = points.slice(1).map((point, n) => [point[0] - points[n][0], point[1] - points[n][1]]);
    pdf.lines(deltas, points[0][0], points[0][1], [1, 1], "F", true);
  };
  const faded = (opacity, draw) => {
    const GState = pdf.GState;
    if (GState) pdf.setGState(new GState({ opacity }));
    draw();
    if (GState) pdf.setGState(new GState({ opacity: 1 }));
  };

  // A chart, drawn: lines, areas and bars on a scale, or a pie - its
  // normal range shaded and days marked, as the window draws it.
  const chart = (visual) => {
    const series = (Array.isArray(visual.series) ? visual.series : []).filter((s) => s && s.key);
    const data = (Array.isArray(visual.data) ? visual.data : []).filter((point) => point && typeof point === "object");
    if (!series.length || !data.length) return;
    const plotH = 150;
    ensure(plotH + 70);
    caption(visual.title, visual.subtitle);
    const top = y;
    const unit = visual.unit;
    if (visual.type === "pie") {
      const key = series[0].key;
      const slices = data.map((point) => ({ label: formatX(point.x, visual.x_kind), value: Number(point[key]) })).filter((s) => s.value > 0);
      const total = slices.reduce((sum, s) => sum + s.value, 0);
      const r = 62;
      const cx = M + r + 8;
      const cy = top + plotH / 2;
      let angle = -Math.PI / 2;
      slices.forEach((slice, n) => {
        const sweep = (slice.value / total) * Math.PI * 2;
        const steps = Math.max(2, Math.ceil(sweep / 0.06));
        const points = [[cx, cy]];
        for (let k = 0; k <= steps; k += 1) points.push([cx + r * Math.cos(angle + (sweep * k) / steps), cy + r * Math.sin(angle + (sweep * k) / steps)]);
        pdf.setFillColor(...SLICE_COLORS[n % SLICE_COLORS.length]);
        polygon(points);
        angle += sweep;
      });
      let ly = top + 6;
      slices.slice(0, 12).forEach((slice, n) => {
        pdf.setFillColor(...SLICE_COLORS[n % SLICE_COLORS.length]);
        pdf.rect(cx + r + 24, ly + 1.5, 7, 7, "F");
        font(8, "normal", INK.text);
        const share = `${Math.round((slice.value / total) * 1000) / 10}%`;
        pdf.text(safe(`${slice.label} - ${formatValue(slice.value, unit)} (${share})`), cx + r + 36, ly, { baseline: "top" });
        ly += 12;
      });
      y = Math.max(top + plotH, ly) + 12;
      return;
    }
    const left = M + 44;
    const right = M + width - 4;
    const plotTop = top + 4;
    const bottom = top + plotH - 16;
    const values = data.flatMap((point) => series.map((s) => point[s.key])).filter(isNumber).map(Number);
    const band = visual.band && isNumber(visual.band.from) && isNumber(visual.band.to) ? visual.band : null;
    if (band) values.push(Number(band.from), Number(band.to));
    const barKeys = series.filter((s) => (s.draw || (visual.type === "bar" ? "bar" : null)) === "bar");
    if (visual.stacked && barKeys.length > 1) {
      data.forEach((point) => values.push(barKeys.reduce((sum, s) => sum + (isNumber(point[s.key]) ? Math.max(0, Number(point[s.key])) : 0), 0)));
    }
    if (!values.length) return;
    let lo = Math.min(0, ...values);
    let hi = Math.max(...values);
    if (hi === lo) hi = lo + 1;
    const step = niceStep(hi - lo);
    lo = Math.floor(lo / step) * step;
    hi = Math.ceil(hi / step) * step;
    const yAt = (value) => bottom - ((value - lo) / (hi - lo)) * (bottom - plotTop);
    const slot = (right - left) / data.length;
    const xAt = (n) => left + slot * (n + 0.5);
    // The scale.
    pdf.setLineWidth(0.5);
    for (let tick = lo; tick <= hi + step / 2; tick += step) {
      pdf.setDrawColor(...INK.line);
      pdf.line(left, yAt(tick), right, yAt(tick));
      font(7, "normal", INK.muted);
      pdf.text(safe(axisValue(tick, unit)), left - 5, yAt(tick), { baseline: "middle", align: "right" });
    }
    if (band) {
      pdf.setFillColor(...PRINT.green);
      faded(0.1, () => pdf.rect(left, yAt(Number(band.to)), right - left, yAt(Number(band.from)) - yAt(Number(band.to)), "F"));
      if (band.label) {
        font(6.5, "normal", PRINT.green);
        pdf.text(safe(band.label), right - 2, yAt(Number(band.to)) + 2, { baseline: "top", align: "right" });
      }
    }
    // Bars, grouped or stacked, then lines and areas over them.
    const group = slot * 0.72;
    const stacked = visual.stacked && barKeys.length > 1;
    const barW = stacked ? group : group / Math.max(1, barKeys.length);
    const heights = data.map(() => 0);
    barKeys.forEach((s, k) => {
      pdf.setFillColor(...seriesColor(s, series.indexOf(s)));
      data.forEach((point, n) => {
        if (!isNumber(point[s.key])) return;
        const value = Number(point[s.key]);
        const base = stacked ? heights[n] : 0;
        const x = xAt(n) - group / 2 + (stacked ? 0 : k * barW);
        const y0 = yAt(base);
        const y1 = yAt(base + value);
        pdf.rect(x, Math.min(y0, y1), Math.max(0.6, barW - (stacked ? 0 : 1)), Math.abs(y1 - y0), "F");
        if (stacked) heights[n] += Math.max(0, value);
      });
    });
    series
      .filter((s) => !barKeys.includes(s))
      .forEach((s) => {
        const color = seriesColor(s, series.indexOf(s));
        const kind = s.draw || (visual.type === "area" ? "area" : "line");
        // Runs of points with values: a line breaks where one has none.
        const runs = [];
        let run = [];
        data.forEach((point, n) => {
          if (isNumber(point[s.key])) run.push([xAt(n), yAt(Number(point[s.key]))]);
          else if (run.length) {
            runs.push(run);
            run = [];
          }
        });
        if (run.length) runs.push(run);
        pdf.setDrawColor(...color);
        pdf.setFillColor(...color);
        runs.forEach((points) => {
          if (s.points || points.length === 1) {
            points.forEach(([x, py]) => pdf.circle(x, py, 1.8, "F"));
            return;
          }
          if (kind === "area") faded(0.16, () => polygon([[points[0][0], yAt(Math.max(lo, 0))], ...points, [points[points.length - 1][0], yAt(Math.max(lo, 0))]]));
          pdf.setLineWidth(1.4);
          if (s.dashed) pdf.setLineDashPattern([4, 3], 0);
          pdf.lines(points.slice(1).map((point, n) => [point[0] - points[n][0], point[1] - points[n][1]]), points[0][0], points[0][1], [1, 1], "S", false);
          if (s.dashed) pdf.setLineDashPattern([], 0);
        });
      });
    // Days marked.
    (Array.isArray(visual.markers) ? visual.markers : []).forEach((marker) => {
      const n = data.findIndex((point) => String(point.x) === String(marker.x));
      if (n === -1) return;
      pdf.setDrawColor(...PRINT.red);
      pdf.setLineWidth(0.8);
      pdf.setLineDashPattern([2, 2], 0);
      pdf.line(xAt(n), plotTop, xAt(n), bottom);
      pdf.setLineDashPattern([], 0);
      if (marker.label) {
        font(6.5, "normal", PRINT.red);
        pdf.text(safe(marker.label), xAt(n) + 2, plotTop, { baseline: "top" });
      }
    });
    // The x axis: some of its labels, evenly.
    pdf.setDrawColor(...INK.faint);
    pdf.line(left, bottom, right, bottom);
    font(7, "normal", INK.muted);
    const every = Math.max(1, Math.ceil(data.length / 7));
    data.forEach((point, n) => {
      if (n % every) return;
      const label = formatX(point.x, visual.x_kind);
      pdf.text(safe(label.length > 14 ? `${label.slice(0, 13)}…` : label), xAt(n), bottom + 4, { baseline: "top", align: "center" });
    });
    y = top + plotH + 4;
    // The legend, for more than one series.
    if (series.length > 1) {
      let x = left;
      series.forEach((s, n) => {
        const label = safe(s.label || s.key);
        font(7.5, "normal", INK.text);
        const w = pdf.getTextWidth(label) + 22;
        if (x + w > right) {
          x = left;
          y += 12;
        }
        pdf.setFillColor(...seriesColor(s, n));
        pdf.rect(x, y + 1.5, 8, 6, "F");
        pdf.text(label, x + 11, y, { baseline: "top" });
        x += w;
      });
      y += 14;
    }
    y += 10;
  };

  const blocks = (list, indent = 0) => list.forEach((block) => blockOf(block, indent));
  const blockOf = (block, indent) => {
    switch (block.type) {
      case "heading":
        y += 3;
        write(plain(block.text), { size: block.level <= 2 ? 13 : 11, style: "bold", indent, gap: 4 });
        break;
      case "paragraph":
        write(block.lines.map(plain).join("\n"), { indent, gap: 6 });
        break;
      case "list":
        block.items.forEach((item, n) => {
          const [first, ...rest] = item;
          const marker = block.ordered ? `${block.start + n}.` : "•";
          if (first?.type === "paragraph") write(first.lines.map(plain).join("\n"), { indent, hang: marker, gap: 2 });
          else blocks([first].filter(Boolean), indent + 14);
          blocks(rest, indent + 14);
        });
        y += 4;
        break;
      case "quote": {
        const from = y;
        const page = pdf.getNumberOfPages();
        blocks(block.children, indent + 12);
        if (pdf.getNumberOfPages() === page) {
          pdf.setDrawColor(...INK.faint);
          pdf.setLineWidth(2);
          pdf.line(M + indent + 3, from, M + indent + 3, y - 6);
        }
        break;
      }
      case "code":
        font(8.5, "normal", INK.text, "courier");
        pdf.splitTextToSize(safe(block.text), width - indent - 16).forEach((line) => {
          ensure(12);
          pdf.setFillColor(...INK.fill);
          pdf.rect(M + indent, y - 1, width - indent, 12, "F");
          font(8.5, "normal", INK.text, "courier");
          pdf.text(line, M + indent + 8, y + 1, { baseline: "top" });
          y += 12;
        });
        y += 8;
        break;
      case "rule":
        rule();
        break;
      case "table": {
        const dataset = markdownTable(block);
        if (dataset) table(dataset.columns.map((c) => c.label), dataset.rows.map((row) => dataset.columns.map((c) => row[c.key])), { align: block.align });
        break;
      }
      case "stats":
        tiles(block.items);
        break;
      case "causes":
        caption("Likely causes");
        table(["Strength", "Cause", "Evidence"], causesDataset(block).rows.map((row) => [row.strength, row.cause, row.evidence]));
        break;
      case "chart":
        if (block.visuals?.[0]) chart(block.visuals[0]);
        break;
      default:
        break;
    }
  };
  const visualOf = (visual) => {
    if (visual.kind === "kpis") {
      if (visual.title) caption(visual.title);
      tiles(Array.isArray(visual.items) ? visual.items : []);
    } else if (visual.kind === "chart") {
      chart(visual);
    } else if (visual.kind === "table") {
      const dataset = tableDataset(visual);
      if (!dataset) return;
      const count = `${dataset.rows.length.toLocaleString()}${visual.more ? "+" : ""} ${dataset.rows.length === 1 && !visual.more ? "row" : "rows"}`;
      caption(dataset.name, [count, visual.subtitle].filter(Boolean).join(" · "));
      table(
        dataset.columns.map((c) => c.label),
        dataset.rows.map((row) => dataset.columns.map((c) => formatCell(row[c.key], c.format))),
        { align: dataset.columns.map((c) => (NUMERIC.has(c.format) ? "right" : null)), note: dataset.note ? `Shown: ${dataset.note}.` : null },
      );
    }
  };

  // The head: whose, what, when, where from.
  font(8, "bold", INK.brand);
  pdf.text("MIZUIQ ANSWER", M, y, { baseline: "top", charSpace: 0.6 });
  font(8, "normal", INK.muted);
  pdf.text("MizuWatch", M + width, y, { baseline: "top", align: "right" });
  y += 16;
  write(doc.heading || doc.question || "MizuIQ answer", { size: 15, style: "bold", gap: 4 });
  if (doc.heading && doc.question && doc.heading !== doc.question) write(`Question: ${doc.question}`, { size: 9.5, color: INK.muted, gap: 3 });
  const meta = [doc.kind, doc.answeredAt ? `Answered ${stamp(doc.answeredAt)}` : null, doc.district ? `District ${doc.district}` : null].filter(Boolean).join("  ·  ");
  if (meta) write(meta, { size: 8.5, color: INK.muted, gap: 2 });
  if (doc.source) write(`Source: ${doc.source}`, { size: 8.5, color: INK.muted, gap: 2 });
  y += 4;
  rule();

  doc.items.forEach((item) => (item.visual ? visualOf(item.visual) : blockOf(item.block, 0)));

  if (doc.sql) {
    y += 4;
    write("Query", { size: 11, style: "bold", gap: 2 });
    write(sqlStatus(doc.sql), { size: 8.5, color: INK.muted, gap: 5 });
    blockOf({ type: "code", text: doc.sql.sql }, 0);
  }
  if (!doc.items.length && !doc.sql) write("(This answer has no text.)", { color: INK.muted });

  // Each page's foot.
  const exported = stamp(new Date());
  const pages = pdf.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page);
    pdf.setDrawColor(...INK.line);
    pdf.setLineWidth(0.5);
    pdf.line(M, H - M + 4, M + width, H - M + 4);
    font(7, "normal", INK.faint);
    pdf.text(safe(`MizuIQ · MizuWatch - exported ${exported}`), M, H - M + 10, { baseline: "top" });
    pdf.text(`Page ${page} of ${pages}`, M + width, H - M + 10, { baseline: "top", align: "right" });
  }
  saveFile(pdf.output("blob"), `${exportName(doc)}.pdf`);
}

// ── Export ──────────────────────────────────────────────────────────────────

/** An answer exported - format "xlsx" | "pdf" | "csv" (one dataset's, by its
 * place in answerDatasets; none: the question and answer). Resolves once the
 * file is handed to the browser. */
export async function exportAnswer(doc, format, datasetIndex = null) {
  const datasets = answerDatasets(doc);
  if (format === "csv") return exportCsv(doc, datasetIndex === null ? null : datasets[datasetIndex]);
  if (format === "xlsx") return exportExcel(doc, datasets);
  if (format === "pdf") return exportPdf(doc);
  throw new Error(`No such export: ${format}`);
}
